import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getApiBaseUrl } from "../utils/apiAssetUrl";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import CryptoJS from "crypto-js";
import Header from "./Header";
import {
  Box,
  Button,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Badge,
  VStack,
  HStack,
  Divider,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Progress,
  Input,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { CheckIcon, TimeIcon } from "@chakra-ui/icons";

const AES_KEY = "ThisIsA32CharacterLongKey123456!";
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_zEWr4dOpfx4w2F";

function decryptPayload(encryptedData, iv) {
  if (!encryptedData || !iv) return "";
  try {
    const keyHex = CryptoJS.enc.Utf8.parse(AES_KEY);
    const ivHex = CryptoJS.enc.Hex.parse(iv);
    const decrypted = CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Hex.parse(encryptedData) }, keyHex, {
      iv: ivHex,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch {
    return "";
  }
}

function parsePlanObject(raw) {
  if (raw == null) return {};
  let v = raw;
  for (let i = 0; i < 2 && typeof v === "string"; i++) {
    try {
      v = JSON.parse(v || "{}");
    } catch {
      return {};
    }
  }
  if (typeof v !== "object" || v === null) return {};
  return v;
}

/** Listing has any plan row (profile-listing or /plans) */
function hasPlanMetadata(po) {
  if (!po || typeof po !== "object" || Object.keys(po).length === 0) return false;
  if (po.plan_name || po.id != null || po.vendorPricingId != null) return true;
  const sale = Number(po.sale_price);
  if (Number.isFinite(sale) && sale > 0) return true;
  if (po.validUntil || po.purchasedAt) return true;
  return false;
}

/** Profile-listing Razorpay path stores `amount` in paise; /plans subscription-paid may send rupees. */
function paymentAmountMatchesPlanSale(paymentAmountRaw, planSaleRaw) {
  const pa = Number(paymentAmountRaw);
  const sp = Number(planSaleRaw);
  if (!Number.isFinite(pa) || !Number.isFinite(sp)) return false;
  if (pa === sp) return true;
  if (pa === sp * 100) return true;
  if (Math.round(pa / 100) === Math.round(sp)) return true;
  return false;
}

/**
 * When store.planObject is empty or incomplete but storepay has Paid rows, infer catalog plan
 * (fixes vendors who paid on create-listing while plan JSON did not persist).
 */
function inferPlanFromPayments(catalog, payments) {
  const rows = Array.isArray(payments) ? payments : [];
  const cat = Array.isArray(catalog) ? catalog : [];
  if (!rows.length || !cat.length) return null;

  const paid = rows.filter((p) => (p.status || "").toLowerCase() === "paid");
  if (!paid.length) return null;

  const sorted = [...paid].sort((a, b) => {
    const tb = new Date(b.endDate || b.startDate || 0).getTime();
    const ta = new Date(a.endDate || a.startDate || 0).getTime();
    return tb - ta;
  });

  for (const p of sorted) {
    const match = cat.find((c) => paymentAmountMatchesPlanSale(p.amount, c.sale_price));
    if (!match) continue;
    const end = p.endDate ? new Date(p.endDate) : null;
    const start = p.startDate ? new Date(p.startDate) : null;
    return {
      id: match.id,
      vendorPricingId: match.id,
      plan_name: match.plan_name,
      validity: match.validity,
      regular_price: match.regular_price,
      sale_price: Number(match.sale_price),
      description: match.description || "",
      purchasedAt: start && Number.isFinite(start.getTime()) ? start.toISOString() : undefined,
      validUntil: end && Number.isFinite(end.getTime()) ? end.toISOString() : undefined,
    };
  }
  return null;
}

/** Paid tier (matches DetailPageVendor-style “free” inverse for tooling) */
function isPaidPriceTier(po) {
  if (!hasPlanMetadata(po)) return false;
  const sale = Number(po.sale_price);
  return Number.isFinite(sale) && sale > 0;
}

/**
 * Plan is currently valid for display. Uses validUntil on store, or latest Paid storepay endDate
 * (profile-listing used to omit validUntil, which made /plans think there was no active plan).
 */
function subscriptionStillValid(po, latestPaidEndIso) {
  if (!hasPlanMetadata(po)) return false;
  const until = po.validUntil || latestPaidEndIso;
  if (!until) return true;
  const end = new Date(until);
  if (!Number.isFinite(end.getTime())) return true;
  return end >= new Date();
}

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function formatMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(x);
}

/**
 * `storepay.amount` mixes units: create-listing / Razorpay order rows use paise (e.g. 49900),
 * some `/plans` flows record rupees (499). Normalize to rupees using catalog prices when possible.
 */
function paymentAmountInRupees(amountRaw, catalog) {
  const n = Number(String(amountRaw ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return NaN;
  const prices = (catalog || [])
    .map((c) => Number(c.sale_price))
    .filter((x) => Number.isFinite(x));
  if (prices.includes(n)) return n;
  for (const r of prices) {
    const paise = Math.round(r * 100);
    if (paise === n) return r;
  }
  if (n >= 1000 && n % 100 === 0) return n / 100;
  return n;
}

function formatPaymentDisplayAmount(amountRaw, catalog) {
  const r = paymentAmountInRupees(amountRaw, catalog);
  if (!Number.isFinite(r)) return "—";
  return formatMoney(r);
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function daysUntil(iso) {
  if (!iso) return null;
  const end = new Date(iso);
  if (!Number.isFinite(end.getTime())) return null;
  const ms = end - Date.now();
  return Math.ceil(ms / (86400000));
}

function normalizeStoreStatus(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
}

/** Status values meaning “not live until admin / ops approves” */
const PENDING_APPROVAL_STATUSES = new Set([
  "pending",
  "pending approval",
  "under review",
  "awaiting approval",
  "in review",
  "verification",
  "awaiting verification",
]);

function isListingPendingApprovalStatus(statusRaw) {
  return PENDING_APPROVAL_STATUSES.has(normalizeStoreStatus(statusRaw));
}

function isListingLiveStatus(statusRaw) {
  return normalizeStoreStatus(statusRaw) === "active";
}

function daysSinceStoreCreated(storeRow) {
  if (!storeRow?.createdAt) return Infinity;
  const d = new Date(storeRow.createdAt);
  if (!Number.isFinite(d.getTime())) return Infinity;
  return (Date.now() - d.getTime()) / 86400000;
}

export default function VendorPlans() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("login") || "{}");
    } catch {
      return {};
    }
  }, []);

  const api = getApiBaseUrl();

  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  /** Prevents double Razorpay opens before React re-renders (Razorpay "Too many requests"). */
  const subscribeLockRef = useRef(false);
  const [catalog, setCatalog] = useState([]);
  const [store, setStore] = useState(null);
  const [payments, setPayments] = useState([]);

  const cancelModal = useDisclosure();
  const [upgradeHintPlan, setUpgradeHintPlan] = useState(null);
  const [bookingTokenInput, setBookingTokenInput] = useState("");
  const [bookingTokenSaving, setBookingTokenSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!login?.permanent_ID) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [plansRes, storeRes, payRes] = await Promise.all([
        fetch(`${api}/vendor-storepricing/all/pricingall`),
        fetch(`${api}/store/getstorelist/mystore/${login.permanent_ID}`),
        axios.get(`${api}/pay-storefees/users/${login.permanent_ID}`).catch(() => ({ data: {} })),
      ]);

      const plansJson = await plansRes.json();
      setCatalog(Array.isArray(plansJson) ? plansJson : []);

      const storeJson = storeRes.ok ? await storeRes.json() : null;
      setStore(storeJson);

      let payList = [];
      const outer = payRes.data?.data;
      const hex = outer?.data ?? outer?.encryptedData;
      const iv = outer?.iv;
      if (hex && iv) {
        const dec = decryptPayload(hex, iv);
        if (dec) {
          try {
            const p = JSON.parse(dec);
            payList = Array.isArray(p) ? p : [];
          } catch {
            payList = [];
          }
        }
      }
      setPayments(payList);
    } catch (e) {
      console.error(e);
      toast({ title: "Could not load plans", description: e?.message, status: "error", duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [api, login?.permanent_ID, toast]);

  useEffect(() => {
    if (location.pathname === "/plans") fetchAll();
  }, [fetchAll, location.pathname]);

  const planObjectFromStore = useMemo(() => parsePlanObject(store?.planObject), [store]);

  const inferredPlan = useMemo(() => inferPlanFromPayments(catalog, payments), [catalog, payments]);

  const planObject = useMemo(() => {
    if (hasPlanMetadata(planObjectFromStore)) return planObjectFromStore;
    if (inferredPlan) return inferredPlan;
    return planObjectFromStore;
  }, [planObjectFromStore, inferredPlan]);

  useEffect(() => {
    const po = parsePlanObject(store?.planObject);
    const t = po?.token_amount ?? po?.tokenAmount;
    setBookingTokenInput(t != null && t !== "" ? String(t) : "");
  }, [store?.id, store?.planObject]);

  const planRepairAttempted = useRef(false);

  useEffect(() => {
    planRepairAttempted.current = false;
  }, [login?.permanent_ID]);

  useEffect(() => {
    if (!login?.permanent_ID || !store?.id || loading) return;
    if (hasPlanMetadata(planObjectFromStore) || !inferredPlan) return;
    if (planRepairAttempted.current) return;
    planRepairAttempted.current = true;
    (async () => {
      try {
        await axios.put(`${api}/store/vendor-plan/by-user/${login.permanent_ID}`, {
          planObject: { ...planObjectFromStore, ...inferredPlan },
        });
        await fetchAll();
      } catch (e) {
        console.error("planObject repair sync failed", e);
        planRepairAttempted.current = false;
      }
    })();
  }, [api, fetchAll, inferredPlan, loading, login?.permanent_ID, planObjectFromStore, store?.id]);

  const storeDisplayName = useMemo(() => {
    if (!store) return "";
    let bd = store.businessDetails;
    if (typeof bd === "string") {
      try {
        bd = JSON.parse(bd || "{}");
      } catch {
        bd = {};
      }
    }
    return bd?.businessName || store.user_name || "";
  }, [store]);

  const sortedCatalog = useMemo(() => {
    return [...catalog].sort((a, b) => Number(a.sale_price || 0) - Number(b.sale_price || 0));
  }, [catalog]);

  const latestPaidEndIso = useMemo(() => {
    const paid = payments.filter((p) => (p.status || "").toLowerCase() === "paid" && p.endDate);
    let max = null;
    for (const p of paid) {
      const e = new Date(p.endDate);
      if (!Number.isFinite(e.getTime())) continue;
      if (!max || e > max) max = e;
    }
    return max ? max.toISOString() : null;
  }, [payments]);

  const effectiveValidUntil = planObject?.validUntil || latestPaidEndIso;
  const currentPlanId = planObject?.id ?? planObject?.vendorPricingId ?? null;
  const currentPlanName = (planObject?.plan_name || "").trim();
  const hasStore = Boolean(store?.id);
  const listingActive = isListingLiveStatus(store?.status);
  const awaitingAdminApproval = hasStore && isListingPendingApprovalStatus(store?.status);
  const recentlyCreatedDeactive =
    hasStore &&
    !awaitingAdminApproval &&
    !listingActive &&
    normalizeStoreStatus(store?.status) === "deactive" &&
    daysSinceStoreCreated(store) <= 21;
  const hasListedPlan = hasPlanMetadata(planObject);
  const isPaidTier = isPaidPriceTier(planObject);
  const subscriptionValid = subscriptionStillValid(planObject, latestPaidEndIso);
  const renewDays = daysUntil(effectiveValidUntil);
  const planExpired = Boolean(
    effectiveValidUntil && Number.isFinite(new Date(effectiveValidUntil).getTime()) && new Date(effectiveValidUntil) < new Date()
  );

  /** Paid rows newest first so “current” subscriptions appear at the top (not buried after slice). */
  const paidSubscriptionHistory = useMemo(() => {
    return [...payments]
      .filter((p) => (p.status || "").toLowerCase() === "paid")
      .sort((a, b) => {
        const tb = new Date(b.endDate || b.startDate || 0).getTime();
        const ta = new Date(a.endDate || a.startDate || 0).getTime();
        if (tb !== ta) return tb - ta;
        const cb = new Date(b.createdAt || 0).getTime();
        const ca = new Date(a.createdAt || 0).getTime();
        return cb - ca;
      });
  }, [payments]);

  const isSubscriptionRowActive = (p) => {
    const end = p.endDate ? new Date(p.endDate) : null;
    return Boolean(end && Number.isFinite(end.getTime()) && end >= new Date());
  };

  const buildPlanPayload = (plan, endDate, previousPlan = {}) => {
    const out = {
      id: plan.id,
      vendorPricingId: plan.id,
      plan_name: plan.plan_name,
      validity: plan.validity,
      regular_price: plan.regular_price,
      sale_price: plan.sale_price,
      description: plan.description || "",
      purchasedAt: new Date().toISOString(),
      validUntil: endDate.toISOString(),
    };
    const ta = previousPlan.token_amount ?? previousPlan.tokenAmount;
    if (ta != null && String(ta).trim() !== "") {
      const n = Number(ta);
      if (Number.isFinite(n) && n >= 0) out.token_amount = n;
    }
    return out;
  };

  const persistPlanOnStore = async (payload, statusOpt) => {
    await axios.put(`${api}/store/vendor-plan/by-user/${login.permanent_ID}`, {
      planObject: payload,
      status: statusOpt ?? undefined,
    });
  };

  const saveBookingToken = async () => {
    const n = Number(String(bookingTokenInput ?? "").replace(/,/g, "").trim());
    if (!Number.isFinite(n) || n < 0) {
      toast({ title: "Enter a valid amount (₹)", status: "warning", duration: 4000 });
      return;
    }
    setBookingTokenSaving(true);
    try {
      const base = parsePlanObject(store?.planObject);
      const merged = { ...base, token_amount: n };
      await axios.put(`${api}/store/vendor-plan/by-user/${login.permanent_ID}`, { planObject: merged });
      toast({
        title: "Booking token saved",
        description: "Location products will require security deposit ≥ this amount when set.",
        status: "success",
        duration: 5000,
      });
      await fetchAll();
    } catch (e) {
      toast({
        title: "Could not save",
        description: e?.response?.data?.message || e?.message,
        status: "error",
        duration: 6000,
      });
    } finally {
      setBookingTokenSaving(false);
    }
  };

  const recordPayment = async (body) => {
    const res = await axios.post(`${api}/pay-storefees/subscription-paid`, body);
    return res.data;
  };

  const handleSubscribe = async (plan) => {
    if (!hasStore) {
      toast({ title: "Create a listing first", status: "warning", duration: 4000 });
      navigate("/profile-listing");
      return;
    }

    if (subscribeLockRef.current) return;
    subscribeLockRef.current = true;
    setPayingId(plan.id);
    const validityDays = Number(plan.validity) || 30;
    const orderId = `pl_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const userPhone = login.name || login.mobile || "";

    const releaseLock = () => {
      subscribeLockRef.current = false;
      setPayingId(null);
    };

    try {
      if (Number(plan.sale_price) === 0) {
        await recordPayment({
          user_id: login.permanent_ID,
          user_phone: userPhone,
          transaction_id: orderId,
          amount: 0,
          validity: validityDays,
          currency: "INR",
          payment_mode: "free",
          status: "Paid",
        });
        const end = new Date();
        end.setDate(end.getDate() + validityDays);
        await persistPlanOnStore(buildPlanPayload(plan, end, planObject), "Active");
        toast({ title: "Plan activated", description: `${plan.plan_name} is now active.`, status: "success", duration: 5000 });
        await fetchAll();
        return;
      }

      const amountPaise = Math.round(Number(plan.sale_price) * 100);
      if (!Number.isFinite(amountPaise) || amountPaise < 100) {
        toast({ title: "Invalid plan amount", status: "error" });
        return;
      }

      /** From POST /pay-storefees/create-order; null = route not deployed → legacy amount-only checkout. */
      let createPayload = null;
      try {
        const co = await axios.post(`${api}/pay-storefees/create-order`, {
          user_id: login.permanent_ID,
          amount: amountPaise,
          currency: "INR",
          receipt: orderId.slice(0, 40),
        });
        if (co.data?.order_id && !co.data.isError) {
          createPayload = co.data;
        }
      } catch (err) {
        console.error(err);
        const st = err?.response?.status;
        const rawData = err?.response?.data;
        const dataStr = typeof rawData === "string" ? rawData : "";
        const missingCreateOrderRoute =
          st === 404 ||
          st === 405 ||
          /cannot post/i.test(dataStr);

        if (missingCreateOrderRoute) {
          console.warn(
            "[plans] /pay-storefees/create-order missing — legacy Razorpay checkout. Deploy Backend storepay.route.js."
          );
        } else {
          const msg =
            (typeof rawData === "object" && rawData?.message) ||
            (typeof rawData === "string" && rawData.slice(0, 200)) ||
            err?.message ||
            "Could not start payment";
          const isRate = typeof msg === "string" && /too many requests/i.test(msg);
          toast({
            title: isRate ? "Payment service busy" : "Could not start payment",
            description: isRate
              ? "Wait 30–60 seconds and try again. Avoid double-clicking Upgrade."
              : String(msg),
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          return;
        }
      }

      const ok = await loadRazorpay();
      if (!ok) {
        toast({ title: "Payment SDK failed to load", status: "error" });
        return;
      }

      const checkoutKey = createPayload?.key_id || RAZORPAY_KEY;

      const gateway = {
        user_id: login.permanent_ID,
        user_phone: userPhone,
        transaction_id: orderId,
        amount: amountPaise,
        validity: validityDays,
        currency: "INR",
        payment_capture: 1,
        payment_mode: "razorpay",
      };
      if (createPayload?.order_id) {
        gateway.razorpay_order_id = createPayload.order_id;
      }

      await new Promise((resolve, reject) => {
        const options = {
          key: checkoutKey,
          currency: "INR",
          name: "Filmekee Listing",
          description: `${plan.plan_name} — ${validityDays} days`,
          handler: async (response) => {
            try {
              gateway.status = "Paid";
              gateway.razorpay_payment_id = response.razorpay_payment_id;
              await recordPayment({
                ...gateway,
                amount: plan.sale_price,
              });
              const end = new Date();
              end.setDate(end.getDate() + validityDays);
              await persistPlanOnStore(buildPlanPayload(plan, end, planObject), "Active");
              toast({
                title:
                  hasListedPlan && subscriptionValid && Number(currentPlanId) !== Number(plan.id)
                    ? "Plan upgraded"
                    : "Payment successful",
                status: "success",
                duration: 5000,
              });
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          prefill: {
            name: login.name || "",
            contact: userPhone,
          },
          theme: { color: "#805AD5" },
          modal: {
            ondismiss: () => resolve(),
          },
        };
        if (createPayload?.order_id) {
          options.order_id = createPayload.order_id;
        } else {
          options.amount = amountPaise;
        }
        const rz = new window.Razorpay(options);
        rz.on("payment.failed", () => {
          toast({ title: "Payment failed", status: "error" });
          resolve();
        });
        rz.open();
      });

      await fetchAll();
    } catch (e) {
      console.error(e);
      toast({
        title: "Could not complete",
        description: e?.response?.data?.message || e.message,
        status: "error",
        duration: 6000,
      });
    } finally {
      releaseLock();
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await axios.put(`${api}/store/vendor-plan/by-user/${login.permanent_ID}`, {
        planObject: {},
        status: "Deactive",
      });
      toast({
        title: "Plan cancelled",
        description: "Your listing is set to inactive. Renew anytime from this page.",
        status: "info",
        duration: 6000,
      });
      cancelModal.onClose();
      await fetchAll();
    } catch (e) {
      toast({ title: "Cancel failed", description: e?.message, status: "error" });
    }
  };

  const planRowMatchesStore = (plan) => {
    if (currentPlanId != null && Number(plan.id) === Number(currentPlanId)) return true;
    if (currentPlanName && plan.plan_name === currentPlanName) return true;
    return false;
  };

  const cardBorder = (plan) => {
    if (planRowMatchesStore(plan)) return "2px solid";
    if (Number(plan.sale_price) > 0 && sortedCatalog.filter((p) => Number(p.sale_price) > 0).slice(-1)[0]?.id === plan.id) {
      return "2px solid";
    }
    return "1px solid";
  };

  const cardBorderColor = (plan) => {
    if (planRowMatchesStore(plan)) return "brand.500";
    const top = sortedCatalog.filter((p) => Number(p.sale_price) > 0).slice(-1)[0];
    if (top && top.id === plan.id) return "purple.300";
    return "gray.200";
  };

  if (!login?.permanent_ID || login.type !== "vendor") {
    return (
      <>
        <Header />
        <Container py={16}>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            Vendor login required to manage plans.
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Box minH="100vh" bg="gray.50" py={{ base: 6, md: 10 }}>
        <Container maxW="1200px">
          <VStack align="stretch" spacing={6}>
            <VStack align="start" spacing={1}>
              <Heading size="lg" color="gray.800">
                Listing plans
              </Heading>
              {/* <Text color="gray.600" fontSize="md" maxW="720px">
                Choose an admin-published plan to activate or upgrade your listing. Renew before expiry to avoid downtime. Cancelling sets your listing to{" "}
                <strong>inactive</strong> until you subscribe again.
              </Text> */}
            </VStack>

            {!hasStore && (
              <Alert status="warning" borderRadius="lg" variant="subtle">
                <AlertIcon />
                <Box>
                  <AlertTitle>No listing yet</AlertTitle>
                  <AlertDescription>
                    Create your store first, then return here to purchase a plan.{" "}
                    <Button as={RouterLink} to="/profile-listing" size="sm" colorScheme="brand" variant="link" ml={1}>
                      Go to create listing
                    </Button>
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {hasStore && awaitingAdminApproval && (
              <Alert status="warning" borderRadius="xl" variant="left-accent" py={4}>
                <AlertIcon boxSize="28px" />
                <Box>
                  <AlertTitle fontSize="md">Listing under verification</AlertTitle>
                  <AlertDescription fontSize="sm" mt={1}>
                    Your listing has been submitted and is waiting for admin review. It will not appear publicly until it is approved and set to{" "}
                    <strong>Active</strong>. You can still purchase or manage a plan here; going live also requires approval.
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {hasStore && recentlyCreatedDeactive && (
              <Alert status="info" borderRadius="lg" variant="subtle">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">Listing not live yet</AlertTitle>
                  <AlertDescription fontSize="sm">
                    If you recently created this listing, it may still be awaiting team review. Older accounts marked inactive may stay off the marketplace until you renew or
                    contact support.
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {hasStore && (
              <Box bg="white" borderRadius="xl" p={{ base: 4, md: 6 }} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                <Heading size="sm" mb={3} color="gray.700">
                  Current status
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Listing
                    </Text>
                    <Text fontWeight="600">{storeDisplayName || "Your listing"}</Text>
                    <HStack mt={2} flexWrap="wrap">
                      {awaitingAdminApproval ? (
                        <Badge colorScheme="orange">Under verification</Badge>
                      ) : (
                        <Badge colorScheme={listingActive ? "green" : "gray"}>{store?.status || "Unknown"}</Badge>
                      )}
                      {!hasListedPlan ? (
                        <Badge colorScheme="gray">No plan on file</Badge>
                      ) : isPaidTier ? (
                        <Badge colorScheme="purple">Paid plan</Badge>
                      ) : (
                        <Badge colorScheme="blue">Free tier</Badge>
                      )}
                    </HStack>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                      Active plan
                    </Text>
                    {hasListedPlan ? (
                      <VStack align="start" spacing={1}>
                        <HStack flexWrap="wrap" align="center" gap={2}>
                          <Text fontWeight="600">{planObject.plan_name || "Plan"}</Text>
                          {planExpired ? (
                            <Badge colorScheme="red">Expired</Badge>
                          ) : subscriptionValid ? (
                            <Badge colorScheme="green">Active</Badge>
                          ) : null}
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {effectiveValidUntil ? (
                            <>
                              Valid until {formatDate(effectiveValidUntil)}
                              {renewDays != null && (
                                <Badge ml={2} colorScheme={renewDays <= 7 ? "red" : renewDays <= 30 ? "orange" : "green"}>
                                  {renewDays < 0 ? "Expired" : `${renewDays}d left`}
                                </Badge>
                              )}
                            </>
                          ) : (
                            <>Validity synced from your listing — renew from the plans below when needed.</>
                          )}
                        </Text>
                        {renewDays != null && renewDays <= 30 && renewDays >= 0 && (
                          <Progress value={Math.min(100, (renewDays / 30) * 100)} size="sm" colorScheme="orange" borderRadius="full" w="full" maxW="280px" />
                        )}
                      </VStack>
                    ) : (
                      <Text color="gray.600" fontSize="sm">
                        No plan is stored on your listing yet. Complete checkout on{" "}
                        <Button as={RouterLink} to="/profile-listing" variant="link" colorScheme="brand" size="sm" p={0} h="auto" minW={0}>
                          create listing
                        </Button>{" "}
                        or choose a plan below.
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>

                <Divider my={4} />
                <FormControl maxW="420px">
                  <FormLabel fontSize="sm" fontWeight="600" mb={1}>
                    Booking token (₹)
                  </FormLabel>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    Minimum amount you expect as token for bookings. For <strong>Locations</strong> listings, each product&apos;s
                    security deposit must be at least this value.
                  </Text>
                  <HStack spacing={2} align="flex-start">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      placeholder="e.g. 5000"
                      value={bookingTokenInput}
                      onChange={(e) => setBookingTokenInput(e.target.value)}
                    />
                    <Button
                      colorScheme="brand"
                      onClick={() => void saveBookingToken()}
                      isLoading={bookingTokenSaving}
                      loadingText="Saving"
                      flexShrink={0}
                    >
                      Save
                    </Button>
                  </HStack>
                </FormControl>

                {hasListedPlan && subscriptionValid && (
                  <HStack mt={4} flexWrap="wrap" gap={2}>
                    <Button size="sm" variant="outline" colorScheme="red" onClick={cancelModal.onOpen}>
                      Cancel plan &amp; deactivate listing
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const higher = sortedCatalog.filter((p) => Number(p.sale_price) > Number(planObject.sale_price || 0));
                        setUpgradeHintPlan(higher[0] || null);
                        toast({
                          title: higher.length ? "Choose a higher plan below" : "You are already on the top tier",
                          status: higher.length ? "info" : "success",
                          duration: 4000,
                        });
                      }}
                    >
                      Upgrade options
                    </Button>
                  </HStack>
                )}
              </Box>
            )}

            {loading ? (
              <HStack justify="center" py={20}>
                <Spinner size="lg" color="brand.500" />
              </HStack>
            ) : sortedCatalog.length === 0 ? (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                No plans published yet. Please ask admin to add plans under vendor pricing.
              </Alert>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {sortedCatalog.map((plan) => {
                  const isCurrent = planRowMatchesStore(plan);
                  const isCheaperThanCurrent =
                    hasListedPlan &&
                    isPaidTier &&
                    Number(plan.sale_price) < Number(planObject.sale_price || 0) &&
                    !isCurrent;
                  const isUpgrade =
                    hasListedPlan &&
                    subscriptionValid &&
                    Number(plan.sale_price) > Number(planObject.sale_price || 0) &&
                    !isCurrent;
                  const busy = payingId === plan.id;

                  return (
                    <Box
                      key={plan.id}
                      bg="white"
                      borderRadius="xl"
                      overflow="hidden"
                      borderWidth={cardBorder(plan)}
                      borderColor={cardBorderColor(plan)}
                      boxShadow={isCurrent ? "lg" : "sm"}
                      position="relative"
                    >
                      {isCurrent && (
                        <Badge position="absolute" top={3} right={3} colorScheme="green" borderRadius="full" px={3}>
                          Current
                        </Badge>
                      )}
                      {upgradeHintPlan?.id === plan.id && (
                        <Badge position="absolute" top={3} left={3} colorScheme="purple" variant="solid">
                          Suggested
                        </Badge>
                      )}
                      <VStack p={6} align="stretch" spacing={4}>
                        <Heading size="md">{plan.plan_name}</Heading>
                        <Box>
                          <HStack align="baseline">
                            <Text fontSize="3xl" fontWeight="800" color="brand.600">
                              {formatMoney(plan.sale_price)}
                            </Text>
                            {Number(plan.regular_price) > Number(plan.sale_price) && (
                              <Text as="span" fontSize="md" color="gray.400" textDecoration="line-through">
                                {formatMoney(plan.regular_price)}
                              </Text>
                            )}
                          </HStack>
                          <HStack color="gray.600" fontSize="sm" mt={1}>
                            <TimeIcon boxSize={3} />
                            <Text>{plan.validity} days validity</Text>
                          </HStack>
                        </Box>
                        {plan.description ? (
                          <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap">
                            {plan.description}
                          </Text>
                        ) : null}
                        <Divider />
                        <VStack align="stretch" spacing={2} fontSize="sm" color="gray.600">
                          <HStack>
                            <CheckIcon color="green.500" />
                            <Text>Leads &amp; contact tools (on paid plans)</Text>
                          </HStack>
                          <HStack>
                            <CheckIcon color="green.500" />
                            <Text>Listing stays active for the validity period</Text>
                          </HStack>
                          <HStack>
                            <CheckIcon color="green.500" />
                            <Text>Upgrade anytime to a higher tier</Text>
                          </HStack>
                        </VStack>
                        <Button
                          colorScheme="brand"
                          size="lg"
                          isLoading={busy}
                          loadingText="Processing…"
                          isDisabled={!hasStore || isCurrent || isCheaperThanCurrent || payingId != null}
                          onClick={() => handleSubscribe(plan)}
                        >
                          {!hasStore
                            ? "Create listing first"
                            : isCurrent
                              ? "Current plan"
                              : isCheaperThanCurrent
                                ? "Downgrade N/A"
                                : isUpgrade
                                  ? "Upgrade"
                                  : hasListedPlan && subscriptionValid
                                    ? "Switch to this plan"
                                    : Number(plan.sale_price) === 0
                                      ? "Activate free"
                                      : "Buy now"}
                        </Button>
                        {isCheaperThanCurrent && (
                          <Text fontSize="xs" color="gray.500">
                            Downgrades are not automated. Cancel and resubscribe, or contact support.
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}

            {paidSubscriptionHistory.length > 0 && (
              <Box bg="white" borderRadius="xl" p={{ base: 4, md: 6 }} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                <Heading size="sm" mb={1}>
                  Subscription payments
                </Heading>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Amounts are shown in rupees. Newest periods first; <strong>Active</strong> means the end date is still in the future.
                </Text>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Transaction</Th>
                        <Th>Date &amp; time</Th>
                        <Th isNumeric>Amount</Th>
                        <Th>Valid until</Th>
                        <Th>Subscription</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paidSubscriptionHistory.slice(0, 25).map((row) => {
                        const active = isSubscriptionRowActive(row);
                        const paidAt = row.createdAt || row.startDate;
                        return (
                          <Tr key={row.id || row.transaction_id}>
                            <Td fontFamily="mono" fontSize="xs">
                              {row.transaction_id}
                            </Td>
                            <Td whiteSpace="nowrap">{formatDate(paidAt)}</Td>
                            <Td isNumeric>{formatPaymentDisplayAmount(row.amount, sortedCatalog)}</Td>
                            <Td>{formatDate(row.endDate)}</Td>
                            <Td>
                              <HStack spacing={2} flexWrap="wrap">
                                <Badge colorScheme="green" textTransform="uppercase">
                                  Paid
                                </Badge>
                                <Badge colorScheme={active ? "purple" : "gray"}>{active ? "Active" : "Ended"}</Badge>
                              </HStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            <Alert status="info" borderRadius="lg" variant="left-accent">
              <AlertIcon />
              <Box>
                <AlertTitle>Payments</AlertTitle>
                <AlertDescription fontSize="sm">
                  Paid plans use Razorpay test/live keys configured on the app. Free plans activate instantly. For billing disputes, keep your transaction id from the table
                  above.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </Container>
      </Box>

      <Modal isOpen={cancelModal.isOpen} onClose={cancelModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4}>
          <ModalHeader>Cancel plan?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600">
              This clears your listing plan and sets status to <strong>Deactive</strong>. Your public listing may stop showing until you subscribe again. Past payments are not
              refunded automatically.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={cancelModal.onClose}>
              Keep plan
            </Button>
            <Button colorScheme="red" onClick={handleCancelSubscription}>
              Yes, cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
